const isBrowser = typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const memoryStore = new Map();

export const storageKeys = {
  filters: "greydates::filters",
  browseCountry: "greydates::browseCountry",
  appLanguage: "greydates::appLanguage",
  profileFilters: "greydates::profileFilters",
  profile: "greydates::profile",
  session: "greydates::session",
  user: "greydates::user",
  role: "greydates::role",
  credits: "greydates::credits",
  accessToken: "greydates::accessToken",
  roleSelectionSeen: "greydates::roleSelectionSeen",
  roleSelectionSkipped: "greydates::roleSelectionSkipped",
  onboardingDraft: "greydates::onboardingDraft",
  onboardingComplete: "greydates::onboardingComplete",
  onboardingResumePath: "greydates::onboardingResumePath",
  profilePhotos: "greydates::profilePhotos",
  ownReels: "greydates::ownReels",
};

const LEGACY_PREFIX = "xdate::";

export const migrateLegacyStorageKeys = () => {
  if (!isBrowser) return;

  Object.values(storageKeys).forEach((newKey) => {
    const suffix = newKey.split("::")[1];
    const legacyKey = `${LEGACY_PREFIX}${suffix}`;

    try {
      const legacyValue = window.localStorage.getItem(legacyKey);
      if (legacyValue !== null && window.localStorage.getItem(newKey) === null) {
        window.localStorage.setItem(newKey, legacyValue);
      }

      const legacySessionValue = window.sessionStorage.getItem(legacyKey);
      if (legacySessionValue !== null && window.sessionStorage.getItem(newKey) === null) {
        window.sessionStorage.setItem(newKey, legacySessionValue);
      }
    } catch {
      // ignore migration errors
    }
  });
};

const writeToMemory = (key, value) => {
  memoryStore.set(key, JSON.stringify(value));
};

const readFromMemory = (key) => {
  if (!memoryStore.has(key)) {
    return null;
  }

  try {
    return JSON.parse(memoryStore.get(key));
  } catch {
    return null;
  }
};

export const saveItem = (key, value) => {
  if (!key) return false;

  if (isBrowser) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
      writeToMemory(key, value);
      return true;
    } catch {
      writeToMemory(key, value);
      return false;
    }
  }

  writeToMemory(key, value);
  return false;
};

export const loadItem = (key, fallback = null) => {
  if (!key) return fallback;

  if (isBrowser) {
    try {
      const storedValue = window.localStorage.getItem(key);
      if (storedValue !== null) {
        return JSON.parse(storedValue);
      }
    } catch {
      // fall through to memory storage
    }
  }

  const memoryValue = readFromMemory(key);
  return memoryValue ?? fallback;
};

export const removeItem = (key) => {
  if (!key) return;

  if (isBrowser) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }

  memoryStore.delete(key);
};

export const clearStorage = () => {
  if (isBrowser) {
    try {
      window.localStorage.clear();
      window.sessionStorage.clear();
    } catch {
      // ignore
    }
  }
  memoryStore.clear();
};

// SessionStorage utilities (for roles and temporary data)
export const saveSessionItem = (key, value) => {
  if (!key) return;

  if (isBrowser) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
      return;
    } catch {
      // fall through to memory storage
    }
  }

  writeToMemory(key, value);
};

export const loadSessionItem = (key, fallback = null) => {
  if (!key) return fallback;

  if (isBrowser) {
    try {
      const storedValue = window.sessionStorage.getItem(key);
      if (storedValue === null) {
        return fallback;
      }
      return JSON.parse(storedValue);
    } catch {
      // fall through to memory storage
    }
  }

  const memoryValue = readFromMemory(key);
  return memoryValue ?? fallback;
};

export const removeSessionItem = (key) => {
  if (!key) return;

  if (isBrowser) {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
  }

  memoryStore.delete(key);
};
